import React, { useContext, useState } from 'react';
import { Box, Button, Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import ImagesContext from '../contexts/ImagesContext';
import { company } from '../data/company';
import { brand } from '../theme';

const useStyles = makeStyles((theme) => ({
  hero: {
    backgroundColor: brand.paper,
    paddingTop: 80 + theme.spacing(8),
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      paddingTop: 64 + theme.spacing(5),
      paddingBottom: theme.spacing(7),
    },
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(6),
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '7fr 5fr',
      gap: theme.spacing(8),
    },
  },
  eyebrow: {
    display: 'flex',
    alignItems: 'center',
    color: brand.accent,
    marginBottom: theme.spacing(3),
    '&::before': {
      content: '""',
      width: 32,
      height: 1,
      backgroundColor: brand.accent,
      marginRight: 12,
    },
  },
  title: {
    marginBottom: theme.spacing(3),
    '& em': {
      fontStyle: 'italic',
      color: brand.accent,
    },
  },
  lead: {
    color: brand.muted,
    fontSize: '1.125rem',
    maxWidth: 520,
    marginBottom: theme.spacing(5),
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2, 4),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  textLink: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: brand.ink,
    fontWeight: 600,
    textDecoration: 'none',
    borderBottom: `1px solid ${brand.ink}`,
    paddingBottom: 2,
    '& svg': {
      fontSize: 18,
      marginLeft: 8,
      transition: 'transform 200ms ease',
    },
    '&:hover': {
      color: brand.accent,
      borderBottomColor: brand.accent,
    },
    '&:hover svg': {
      transform: 'translateX(3px)',
    },
    [theme.breakpoints.down('xs')]: {
      alignSelf: 'center',
    },
  },
  figure: {
    position: 'relative',
    margin: 0,
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: theme.spacing(2),
      left: theme.spacing(2),
      right: 0,
      bottom: 0,
      backgroundColor: brand.accentSoft,
    },
  },
  photo: {
    position: 'relative',
    display: 'block',
    width: '100%',
    aspectRatio: '4 / 5',
    objectFit: 'cover',
    backgroundColor: brand.stone,
    [theme.breakpoints.down('sm')]: {
      aspectRatio: '4 / 3',
    },
  },
  photoPlaceholder: {
    position: 'relative',
    width: '100%',
    aspectRatio: '4 / 5',
    backgroundColor: brand.stone,
    backgroundImage: `repeating-linear-gradient(135deg, transparent 0 14px, rgba(28,27,25,0.05) 14px 15px)`,
    [theme.breakpoints.down('sm')]: {
      aspectRatio: '4 / 3',
    },
  },
  caption: {
    position: 'absolute',
    left: 0,
    bottom: theme.spacing(2),
    transform: 'translateY(50%)',
    backgroundColor: brand.ink,
    color: '#FFFFFF',
    padding: theme.spacing(1.25, 2),
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },
  facts: {
    marginTop: theme.spacing(8),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${brand.line}`,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      marginTop: theme.spacing(6),
    },
  },
  fact: {
    padding: theme.spacing(0, 3),
    borderLeft: `1px solid ${brand.line}`,
    '&:first-child': {
      paddingLeft: 0,
      borderLeft: 0,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5, 0),
      borderLeft: 0,
      borderTop: `1px solid ${brand.line}`,
      '&:first-child': {
        borderTop: 0,
        paddingTop: 0,
      },
    },
  },
  factLabel: {
    color: brand.muted,
    display: 'block',
  },
  factValue: {
    fontWeight: 500,
  },
}));

const facts = [
  { label: 'Estimates', value: 'Always free' },
  { label: 'Scope', value: 'Projects big & small' },
  { label: 'Service area', value: 'Seattle & surrounding' },
];

export default function Hero() {
  const classes = useStyles();
  const { imageURLs = [] } = useContext(ImagesContext) || {};
  const [photoFailed, setPhotoFailed] = useState(false);
  const photo = !photoFailed && imageURLs[0];

  return (
    <Box component="header" id="top" className={classes.hero}>
      <Container maxWidth="lg">
        <div className={classes.grid}>
          <div>
            <Typography variant="overline" component="p" className={classes.eyebrow}>
              General contractor · {company.area}
            </Typography>
            <Typography variant="h1" className={classes.title}>
              Projects big and small, <em>built right.</em>
            </Typography>
            <Typography className={classes.lead}>
              {company.name} takes on construction work of every size, from a single repair to a
              full build-out, with one team that has the resources to see it through.
            </Typography>
            <div className={classes.actions}>
              <Button
                href="#quote-form"
                size="large"
                variant="contained"
                color="primary"
                endIcon={<ArrowForwardIcon />}>
                Request a free quote
              </Button>
              <a href={company.phoneHref} className={classes.textLink}>
                Call {company.phoneDisplay}
                <ArrowForwardIcon />
              </a>
            </div>
          </div>

          <figure className={classes.figure}>
            {photo ? (
              <img
                src={photo}
                alt="A recent 3R Construction project"
                className={classes.photo}
                onError={() => setPhotoFailed(true)}
              />
            ) : (
              <div className={classes.photoPlaceholder} aria-hidden="true" />
            )}
            <figcaption className={classes.caption}>Recent work</figcaption>
          </figure>
        </div>

        <div className={classes.facts}>
          {facts.map((fact) => (
            <div key={fact.label} className={classes.fact}>
              <Typography variant="overline" component="span" className={classes.factLabel}>
                {fact.label}
              </Typography>
              <Typography className={classes.factValue}>{fact.value}</Typography>
            </div>
          ))}
        </div>
      </Container>
    </Box>
  );
}
